/* __next_internal_client_entry_do_not_use__ Default auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Local
import { withStandardComponentWrapper } from 'helpers/HOC';
import { PlaceholderWrapper } from 'helpers/SitecoreWrappers/PlaceholderWrapper/PlaceholderWrapper';
import { tv } from 'tailwind-variants';
import { BrandAndThemeProvider } from 'lib/context/BrandAndThemeContext';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { TextAlignmentProvider } from 'lib/context/TextAlignmentContext';
import { IsDataAvailableProvider, useIsDataAvailable } from 'lib/context/IsDataAvailableContext';
import { getTestProps } from 'lib/testing/utils';
import { getCtaStyle } from 'lib/utils/cta-utils';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { parseStyleParams } from 'lib/utils/style-param-utils';
import { createCTAClickGTMEvent } from 'lib/utils/gtm-utils';
import useIsEditing from 'lib/hooks/useIsEditing';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
const Section = (props)=>{
    const { DynamicPlaceholderId, RenderingIdentifier, GAComponentName = 'Section' } = props?.params || {};
    const { title, description, removePaddingTop, removePaddingBottom, removePaddingLeftRight, displayCenter75 } = props?.fields || {};
    const theme = props?.params?.selectTheme;
    const textAlignment = props?.params?.alignment || 'Left';
    const hasContent = Boolean(title?.value || description?.value || props?.fields?.link?.value?.href || props?.fields?.link2?.value?.href);
    const noPaddingTop = Boolean(removePaddingTop?.value);
    const noPaddingBottom = Boolean(removePaddingBottom?.value);
    const noPaddingSides = Boolean(removePaddingLeftRight?.value);
    const isCentered75 = Boolean(displayCenter75?.value);
    // Provider wraps content so children (e.g., NewsList) can set availability and hide this section.
    const phKey = `section-${DynamicPlaceholderId}`;
    const styles = parseStyleParams(props.params, [
        'cta1',
        'cta2'
    ]);
    const hasCtas = Boolean(props?.fields?.link?.value?.href || props?.fields?.link2?.value?.href);
    const isEditing = useIsEditing();
    const { base, sectionTitle, sectionDescription, sectionContentWrapper, sectionTitleWrapper, sectionDivider, buttonWrapper, sectionWrapper } = TAILWIND_VARIANTS({
        textAlignment
    });
    /*
   * RENDERING
   */ const SectionContent = ()=>{
        const isDataAvailable = useIsDataAvailable();
        if (!isEditing && !isDataAvailable) return /*#__PURE__*/ _jsx(_Fragment, {});
        return /*#__PURE__*/ _jsx("div", {
            "data-component": "authorable/shared/layout/section",
            ...editingHydrationProps(isEditing),
            id: RenderingIdentifier,
            ...getTestProps(`component-section-${props?.rendering?.uid}`),
            className: sectionWrapper(),
            children: /*#__PURE__*/ _jsx(BrandAndThemeProvider, {
                theme: theme,
                children: /*#__PURE__*/ _jsx(TextAlignmentProvider, {
                    textAlignment: textAlignment,
                    children: /*#__PURE__*/ _jsx("div", {
                        className: base({
                            textAlignment,
                            removePaddingTop: noPaddingTop,
                            removePaddingBottom: noPaddingBottom,
                            removePaddingLeftRight: noPaddingSides
                        }),
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: sectionContentWrapper({
                                textAlignment,
                                hasContent,
                                displayCenter75: isCentered75
                            }),
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: sectionTitleWrapper({
                                        textAlignment
                                    }),
                                    children: [
                                        title?.value?.trim() && /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                            className: sectionTitle(),
                                            field: title,
                                            tag: "h2"
                                        }),
                                        /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                            className: sectionDescription(),
                                            field: description
                                        }),
                                        title?.value || description?.value ? /*#__PURE__*/ _jsx("span", {
                                            className: sectionDivider(),
                                            "data-alignment": textAlignment
                                        }) : null
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(PlaceholderWrapper, {
                                    name: phKey,
                                    rendering: props.rendering
                                }),
                                hasCtas && /*#__PURE__*/ _jsxs("div", {
                                    className: buttonWrapper(),
                                    children: [
                                        /*#__PURE__*/ _jsx(LinkWrapper, {
                                            ctaComponentClass: "component-card-item-button-2-color",
                                            ctaStyle: getCtaStyle(styles.cta1, 'primary'),
                                            field: props?.fields?.link,
                                            ctaIcon: "arrow-right",
                                            ...getTestProps(`secondary-cta`),
                                            gtmEvent: createCTAClickGTMEvent({
                                                header: title?.value,
                                                ctaText: props?.fields?.link?.value?.text,
                                                ctaType: 'blue',
                                                componentName: GAComponentName,
                                                ctaLink: props?.fields?.link?.value?.href
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(LinkWrapper, {
                                            ctaComponentClass: "component-card-item-button-2-color",
                                            ctaStyle: getCtaStyle(styles.cta2, 'secondary'),
                                            field: props?.fields?.link2,
                                            ctaIcon: "arrow-right",
                                            ...getTestProps(`secondary-cta`),
                                            gtmEvent: createCTAClickGTMEvent({
                                                header: title?.value,
                                                ctaText: props?.fields?.link2?.value?.text,
                                                ctaType: 'clear',
                                                componentName: GAComponentName,
                                                ctaLink: props?.fields?.link2?.value?.href
                                            })
                                        })
                                    ]
                                })
                            ]
                        })
                    })
                })
            })
        });
    };
    return /*#__PURE__*/ _jsx(IsDataAvailableProvider, {
        children: /*#__PURE__*/ _jsx(SectionContent, {})
    });
};
export const Default = withStandardComponentWrapper(Section, false);
const TAILWIND_VARIANTS = tv({
    slots: {
        sectionWrapper: [
            'scroll-mt-[64px]',
            'md:scroll-mt-0'
        ],
        base: [
            'flex',
            'bg-component-section-bg',
            'gap-component-section-container-gutter',
            'flex-wrap',
            'relative'
        ],
        sectionTitleWrapper: [
            'flex',
            'flex-col',
            'gap-space-between-base',
            'max-w-columns-two-third-max-width'
        ],
        sectionTitle: [
            'text-component-section-title',
            'font-typography-font-family-header',
            'text-typography-font-size-xlarge',
            'leading-typography-line-height-xlarge'
        ],
        sectionDescription: [
            'text-component-section-body',
            'font-typography-font-family-body',
            'text-typography-font-size-body-large',
            'leading-typography-line-height-body-large'
        ],
        sectionContentWrapper: [
            'flex',
            'flex-col',
            'w-full',
            'h-auto'
        ],
        sectionDivider: [
            'w-component-section-divider-width',
            'h-component-section-divider-height',
            'bg-component-section-divider'
        ],
        buttonWrapper: [
            'flex',
            'gap-spacing-spacing-16',
            'flex-col',
            'md:flex-row'
        ]
    },
    variants: {
        textAlignment: {
            Left: {
                base: 'text-left'
            },
            Center: {
                base: 'text-center',
                sectionTitleWrapper: 'justify-center mx-auto',
                sectionContentWrapper: 'justify-center',
                sectionDivider: 'mx-auto',
                buttonWrapper: 'justify-center'
            },
            Right: {
                base: 'text-right',
                sectionTitleWrapper: 'justify-end',
                sectionContentWrapper: 'justify-end',
                sectionDivider: 'ml-auto',
                buttonWrapper: 'justify-end'
            }
        },
        backgroundLayout: {
            FullWidth: {
                base: [
                    'before:z-[-1]',
                    'before:h-full',
                    'before:absolute',
                    'before:bg-component-section-bg',
                    'before:top-0',
                    'before:left-[calc(-50vw+50%)]',
                    'before:right-[calc(-50vw+50%)]'
                ]
            },
            Content: {
                base: []
            }
        },
        hasContent: {
            true: {
                sectionContentWrapper: 'gap-component-section-container-gutter'
            },
            false: {
                sectionContentWrapper: 'gap-0'
            }
        },
        removePaddingTop: {
            true: {
                base: [
                    'pt-0'
                ]
            },
            false: {
                base: [
                    'pt-component-section-container-padding-y'
                ]
            }
        },
        removePaddingBottom: {
            true: {
                base: [
                    'pb-0'
                ]
            },
            false: {
                base: [
                    'pb-component-section-container-padding-y'
                ]
            }
        },
        removePaddingLeftRight: {
            true: {
                base: []
            },
            false: {
                base: [
                    'px-component-section-container-padding-x'
                ]
            }
        },
        displayCenter75: {
            true: {
                sectionContentWrapper: [
                    'max-w-layout-container-center70-max-width',
                    'mx-auto'
                ]
            },
            false: {
                sectionContentWrapper: []
            }
        }
    },
    defaultVariants: {
        textAlignment: 'Left',
        hasContent: false,
        removePaddingTop: false,
        removePaddingBottom: false,
        removePaddingLeftRight: false,
        displayCenter75: false,
        backgroundLayout: 'FullWidth'
    }
});
